<template>
  <main class="tool-page tool-page-wide">
    <div class="json-grid">
      <el-card shadow="never" class="panel">
        <div slot="header" class="panel-head">
          <div>
            <p class="panel-kicker">JSON</p>
            <h2>输入与操作</h2>
          </div>
          <div class="toolbar-inline">
            <el-button plain @click="fillSampleJson">示例</el-button>
            <el-button plain @click="clearJson">清空</el-button>
          </div>
        </div>

        <el-form label-position="top" @submit.native.prevent>
          <p class="result-label">输入 JSON 或 JSON 字符串</p>
          <el-form-item class="input-area-item">
            <el-input
              v-model="jsonInput"
              type="textarea"
              :rows="16"
              resize="vertical"
              placeholder='例如 {"name":"tool","path":"C:\\temp\\demo"}'
              @keydown.native="handleJsonKeydown"
            />
          </el-form-item>

          <div class="option-row">
            <el-checkbox v-model="parseStringJson">如果输入本身是 JSON 字符串，则自动解开再格式化</el-checkbox>
            <el-checkbox v-model="copyEscaped">复制时保留转义（输出为 JSON 字符串）</el-checkbox>
          </div>

          <div class="actions">
            <el-button type="primary" @click="formatJson">格式化 JSON</el-button>
          </div>
        </el-form>
      </el-card>

      <el-card shadow="never" class="panel">
        <div slot="header" class="panel-head">
          <div>
            <p class="panel-kicker">Result</p>
            <h2>格式化结果</h2>
          </div>
          <div class="toolbar-inline">
            <el-button @click="copyJsonOutput">复制结果</el-button>
          </div>
        </div>

        <div class="result-stack">
          <div class="tree-panel">
            <p class="result-label">可折叠结构视图</p>
            <div v-if="parsedJsonReady" class="tree-view">
              <JsonTreeNode :value="parsedJson" />
            </div>
            <div v-else class="tree-empty"></div>
          </div>
        </div>
      </el-card>
    </div>
  </main>
</template>

<script>
import JsonTreeNode from "../components/JsonTreeNode.vue";

const tryParseJson = (raw) => {
  const parsed = JSON.parse(raw);
  if (typeof parsed === "string") {
    return JSON.parse(parsed);
  }
  return parsed;
};

export default {
  name: "JsonPage",
  components: {
    JsonTreeNode,
  },
  data() {
    return {
      jsonInput: "",
      jsonOutput: "",
      parsedJson: null,
      parsedJsonReady: false,
      parseStringJson: true,
      copyEscaped: false,
      jsonStatus: {
        message: "等待输入",
        type: "",
      },
    };
  },
  watch: {
    jsonInput() {
      this.formatJson(true);
    },
    parseStringJson() {
      this.formatJson(true);
    },
  },
  methods: {
    setStatus(message, type) {
      this.jsonStatus = {
        message,
        type: type || "",
      };
    },
    formatJson(isAuto = false) {
      const raw = this.jsonInput.trim();
      if (!raw) {
        this.jsonOutput = "";
        this.parsedJson = null;
        this.parsedJsonReady = false;
        this.setStatus("请输入 JSON 内容", isAuto ? "" : "error");
        return;
      }

      try {
        let parsed = JSON.parse(raw);
        if (typeof parsed === "string" && this.parseStringJson) {
          parsed = tryParseJson(raw);
        }
        this.parsedJson = parsed;
        this.parsedJsonReady = true;
        this.jsonOutput = JSON.stringify(parsed, null, 2);
        this.setStatus(isAuto ? "已自动格式化 JSON" : "JSON 已格式化，可直接复制", "success");
      } catch (error) {
        this.parsedJson = null;
        this.parsedJsonReady = false;
        this.jsonOutput = "";
        this.setStatus(`格式化失败：${error.message}`, "error");
      }
    },
    async copyText(text, successMessage) {
      const value = (text || "").trim();
      if (!value) {
        this.$message.error("没有可复制的内容");
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
        this.$message.success(successMessage);
      } catch (error) {
        this.$message.error("复制失败，请检查浏览器权限");
      }
    },
    async copyJsonOutput() {
      const output = this.jsonOutput.trim();
      const copyValue = this.copyEscaped ? JSON.stringify(output) : output;
      const message = this.copyEscaped ? "已复制带转义的 JSON 字符串" : "已复制格式化结果";
      await this.copyText(copyValue, message);
      if (output) {
        this.setStatus(message, "success");
      }
    },
    fillSampleJson() {
      this.jsonInput = JSON.stringify(
        {
          name: "dev-toolbox",
          enabled: true,
          path: "C:\\temp\\demo",
          items: [{ id: 1, label: "timestamp" }, { id: 2, label: "json" }],
        },
        null,
        2
      );
      this.formatJson(true);
    },
    clearJson() {
      this.jsonInput = "";
      this.jsonOutput = "";
      this.parsedJson = null;
      this.parsedJsonReady = false;
      this.setStatus("已清空 JSON 输入与结果", "success");
    },
    handleJsonKeydown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        this.formatJson();
      }
    },
  },
};
</script>

<style scoped>
.tool-page-wide {
  max-width: 1200px;
}

.json-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.json-grid .panel {
  height: 100%;
}

.json-grid .panel .el-card__body {
  display: flex;
  flex-direction: column;
}

.json-grid .el-form {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.json-grid .input-area-item,
.json-grid .el-form-item:first-child {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;
}

.json-grid .input-area-item .el-form-item__content,
.json-grid .el-form-item:first-child .el-form-item__content {
  flex: 1;
  display: flex;
}

.json-grid .input-area-item .el-textarea,
.json-grid .input-area-item .el-textarea__inner,
.json-grid .el-form-item:first-child .el-textarea,
.json-grid .el-form-item:first-child .el-textarea__inner {
  height: 100%;
}

.result-stack {
  display: grid;
  gap: 18px;
  height: 100%;
}

.result-label {
  margin: 0 0 10px;
  color: #7b6855;
  font-size: 0.95rem;
}

.tree-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.tree-view,
.tree-empty {
  flex: 1;
  min-height: 365px;
  overflow: auto;
  padding: 16px;
  border: 1px solid rgba(74, 56, 33, 0.12);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
}

.tree-empty {
  display: flex;
  align-items: center;
  color: #7b6855;
  line-height: 1.7;
}

@media (max-width: 960px) {
  .json-grid {
    grid-template-columns: 1fr;
  }
}
</style>
